<template>
  <div class="sort-builder">
    <div v-if="!sorts.length" class="sort-empty">
      no sort columns. records use the default order.
    </div>

    <div v-for="(sort, idx) in sorts" :key="sort.column + '-' + idx" class="sort-row">
      <span class="sort-rank" :title="'precedence ' + (idx + 1)">{{ idx + 1 }}</span>
      <select
        class="sort-select sort-column"
        :value="sort.column"
        @change="updateColumn(idx, $event.target.value)"
      >
        <option v-for="option in optionsFor(idx)" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
      <select
        class="sort-select sort-direction"
        :value="sort.direction === 'desc' ? 'desc' : 'asc'"
        @change="updateDirection(idx, $event.target.value)"
      >
        <option value="asc">ascending</option>
        <option value="desc">descending</option>
      </select>
      <button type="button" class="sort-icon-btn" :disabled="idx === 0" title="Move earlier" @click="move(idx, -1)">↑</button>
      <button type="button" class="sort-icon-btn" :disabled="idx === sorts.length - 1" title="Move later" @click="move(idx, 1)">↓</button>
      <button type="button" class="sort-icon-btn sort-remove" title="Remove sort column" @click="remove(idx)">×</button>
    </div>

    <button type="button" class="sort-add" :disabled="!unusedColumns.length" @click="add">
      + add sort column
    </button>
    <p v-if="sorts.length > 1" class="sort-hint">earlier columns take precedence. later columns break ties.</p>
  </div>
</template>

<script>
  import { sanitizeSorts, sortColumnOptions } from '../../../../shared/view-sort.js'

  export default {
    name: 'SortBuilder',
    props: {
      sorts: { type: Array, default: () => [] },
      spreadsheet: { type: Object, default: null }
    },
    computed: {
      columnOptions () {
        return sortColumnOptions(this.spreadsheet && this.spreadsheet.columns)
      },
      unusedColumns () {
        const used = new Set((this.sorts || []).map((sort) => sort.column))
        return this.columnOptions.filter((option) => !used.has(option.value))
      }
    },
    methods: {
      optionsFor (idx) {
        const used = new Set()
        this.sorts.forEach((sort, sortIndex) => {
          if (sortIndex !== idx && sort && sort.column) used.add(sort.column)
        })
        const options = this.columnOptions.filter((option) => !used.has(option.value))
        const current = this.sorts[idx] && this.sorts[idx].column
        if (current && !options.some((option) => option.value === current)) {
          options.unshift({ value: current, text: current })
        }
        return options
      },
      emitSorts (sorts) {
        this.$emit('update:sorts', sanitizeSorts(sorts))
      },
      updateColumn (idx, column) {
        const next = this.sorts.map((sort) => ({ ...sort }))
        next[idx] = { ...next[idx], column }
        this.emitSorts(next)
      },
      updateDirection (idx, direction) {
        const next = this.sorts.map((sort) => ({ ...sort }))
        next[idx] = { ...next[idx], direction }
        this.emitSorts(next)
      },
      move (idx, delta) {
        const target = idx + delta
        if (target < 0 || target >= this.sorts.length) return
        const next = this.sorts.map((sort) => ({ ...sort }))
        const [item] = next.splice(idx, 1)
        next.splice(target, 0, item)
        this.emitSorts(next)
      },
      remove (idx) {
        const next = this.sorts.filter((_, sortIndex) => sortIndex !== idx)
        this.emitSorts(next)
      },
      add () {
        const nextColumn = this.unusedColumns[0]
        if (!nextColumn) return
        this.emitSorts(this.sorts.concat([{ column: nextColumn.value, direction: 'asc' }]))
      }
    }
  }
</script>

<style scoped>
.sort-builder {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sort-empty {
  color: #6a6a67;
  font-size: 0.8em;
  padding: 8px 0 2px;
}
.sort-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sort-rank {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: rgba(154, 209, 43, 0.12);
  border: 1px solid rgba(154, 209, 43, 0.3);
  color: #9ad12b;
  font-size: 0.75em;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sort-select {
  padding: 6px 10px;
  background: #141412;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  color: #e8e8e6;
  font-size: 0.85rem;
  outline: none;
  font-family: inherit;
}
.sort-select:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.12);
}
.sort-column {
  flex: 1;
  min-width: 0;
}
.sort-direction {
  flex: 0 0 140px;
}
.sort-icon-btn {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  background: #1e1e1b;
  color: #b0b0ae;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}
.sort-icon-btn:hover:not(:disabled) {
  border-color: #9ad12b;
  color: #9ad12b;
}
.sort-icon-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.sort-remove {
  color: #f82471;
}
.sort-remove:hover:not(:disabled) {
  background: #f82471;
  border-color: #f82471;
  color: #fff;
}
.sort-add {
  align-self: flex-start;
  color: #9ad12b;
  cursor: pointer;
  font-size: 0.82em;
  border: 1px dashed #3a3a38;
  border-radius: 5px;
  padding: 8px 16px;
  margin-top: 4px;
  background: transparent;
  font-family: inherit;
}
.sort-add:hover:not(:disabled) {
  color: #b8f046;
  background-color: #1e1e1b;
  border-color: #9ad12b;
}
.sort-add:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.sort-hint {
  color: #6a6a67;
  font-size: 0.78em;
  margin: 0;
  line-height: 1.4;
}
</style>
